/**
 * Map Cognito / amazon-cognito-identity-js errors to user-facing login messages.
 *
 * Requires app client PreventUserExistenceErrors=LEGACY (not ENABLED) so UserNotFoundException
 * is returned for unknown emails (see RBAC_COGNITO_DEPLOYMENT.md).
 *
 * Sign-in lockout: Cognito allows ~5 failed password attempts, then temporary lockouts
 * (exponential backoff up to ~15 minutes). Errors include TooManyFailedAttemptsException
 * or NotAuthorizedException with "Password attempts exceeded".
 */

/** Cognito max temporary lockout window (AWS docs; subject to change). */
export const COGNITO_DEFAULT_LOCKOUT_SECONDS = 15 * 60;

const USER_NOT_FOUND_CODES = new Set([
  "UserNotFoundException",
  "UserNotFound",
]);

const NOT_AUTHORIZED_CODES = new Set([
  "NotAuthorizedException",
  "NotAuthorized",
]);

const NOT_CONFIRMED_CODES = new Set([
  "UserNotConfirmedException",
  "UserNotConfirmed",
]);

const PASSWORD_RESET_REQUIRED_CODES = new Set([
  "PasswordResetRequiredException",
]);

const TOO_MANY_REQUESTS_CODES = new Set([
  "TooManyRequestsException",
  "LimitExceededException",
]);

const LOCKOUT_ERROR_CODES = new Set([
  "TooManyFailedAttemptsException",
  "TooManyFailedAttempts",
]);

const LOCKOUT_MESSAGE_RE =
  /password attempts exceeded|attempt limit exceeded|too many failed attempts|try after some time/i;

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isCognitoSignInLockout(err) {
  const code = String(err?.code || err?.name || "").trim();
  const message = String(err?.message || "").trim();

  if (LOCKOUT_ERROR_CODES.has(code)) {
    return true;
  }

  if (LOCKOUT_MESSAGE_RE.test(message)) {
    return true;
  }

  if (
    NOT_AUTHORIZED_CODES.has(code) &&
    /password attempts exceeded/i.test(message)
  ) {
    return true;
  }

  return false;
}

/**
 * Best-effort retry window when Cognito does not return an exact duration.
 * @param {unknown} [_err]
 * @returns {number} seconds
 */
export function getCognitoLockoutRetrySeconds(_err) {
  return COGNITO_DEFAULT_LOCKOUT_SECONDS;
}

/**
 * @param {number} lockoutUntilMs
 * @returns {string} e.g. "14:32"
 */
export function formatLockoutCountdown(lockoutUntilMs) {
  const remainingMs = Math.max(0, lockoutUntilMs - Date.now());
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * @param {unknown} err
 * @returns {{ message: string, lockoutUntil: number | null, retryAfterSeconds: number | null }}
 */
export function resolveCognitoSignInFailure(err) {
  if (isCognitoSignInLockout(err)) {
    const retryAfterSeconds = getCognitoLockoutRetrySeconds(err);
    const minutes = Math.ceil(retryAfterSeconds / 60);
    return {
      message: `Too many failed sign-in attempts. Wait about ${minutes} minutes, then try again or use Forgot password.`,
      lockoutUntil: Date.now() + retryAfterSeconds * 1000,
      retryAfterSeconds,
    };
  }

  return {
    message: formatCognitoAuthError(err, "signIn"),
    lockoutUntil: null,
    retryAfterSeconds: null,
  };
}

/**
 * @param {unknown} err — Cognito SDK error
 * @param {"signIn" | "forgotPassword" | "resetPassword" | "signUp"} [context]
 * @returns {string}
 */
export function formatCognitoAuthError(err, context = "signIn") {
  const code = String(err?.code || err?.name || "").trim();
  const message = String(err?.message || "").trim();

  if (context === "signIn" && isCognitoSignInLockout(err)) {
    return resolveCognitoSignInFailure(err).message;
  }

  if (USER_NOT_FOUND_CODES.has(code)) {
    if (context === "forgotPassword") {
      return "No Halo account exists for this email. Ask an administrator to invite you.";
    }
    return "No account exists for this email in Halo. Contact an administrator to get invited.";
  }

  if (NOT_CONFIRMED_CODES.has(code)) {
    return "Your account is not confirmed yet. Check your email or ask an administrator.";
  }

  if (PASSWORD_RESET_REQUIRED_CODES.has(code)) {
    return "Password reset is required. Use Forgot password to set a new one.";
  }

  if (TOO_MANY_REQUESTS_CODES.has(code)) {
    if (context === "forgotPassword" || context === "resetPassword") {
      return "Too many reset attempts. Wait about 15 minutes, then try again.";
    }
    return "Too many attempts. Wait a few minutes and try again.";
  }

  if (NOT_AUTHORIZED_CODES.has(code)) {
    if (context === "signIn") {
      return "Incorrect password for this account.";
    }
    if (context === "resetPassword") {
      return "Invalid verification code or password. Check the code and try again.";
    }
    return message || "Not authorized. Check your details and try again.";
  }

  if (/incorrect username or password/i.test(message)) {
    // Fallback when pool still masks UserNotFound (PreventUserExistenceErrors=ENABLED)
    return "Sign in failed. Check your email and password, or ask an administrator if you need an invite.";
  }

  return message || "Sign in failed. Please try again.";
}
